import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Download, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { api, errMsg, downloadText } from "@/lib/api";
import { gbp } from "@/lib/format";
import { Button } from "@/components/ui/button";

const COLS = ["Date Ordered (YYYY/MM/DD)", "Order Number", "Delivery Name (S{store} DD/MM/YYYY)", "Product Reference (SAP code)", "Qty", "Price",
  "Account Customer Reference", "Cost Centre", "Department", "User Defined 2 (PO ref)", "User Defined 3 (Concession code)", "PrefName ({store}HARRODS)"];

const Result = ({ res }) => (
  <div className="mt-4 space-y-2 text-sm" data-testid="upload-results">
    {res.created.map((c) => (
      <div key={c.id} className="flex items-start gap-2 rounded-md bg-emerald-50 p-2.5 text-emerald-900" data-testid={`upload-created-${c.id}`}>
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="flex-1"><b className="font-mono">{c.order_number}</b> — {c.concession_name || c.store_name} · {c.line_count} lines · {gbp(c.total)}
          {c.warnings.length > 0 && <div className="mt-1 flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="h-3 w-3" />{c.warnings.length} warning(s) — review before approving</div>}
        </div>
      </div>))}
    {res.errors.map((e, i) => (
      <div key={i} className="flex items-start gap-2 rounded-md bg-red-50 p-2.5 text-red-800" data-testid="upload-error"><XCircle className="mt-0.5 h-4 w-4 shrink-0" />{e}</div>))}
  </div>
);

export const UploadZone = ({ onUploaded }) => {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const send = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data } = await api.post("/web-orders/upload", fd);
      setRes(data);
      if (data.created.length) toast.success(`${data.created.length} order(s) added to the approval queue`);
      if (data.errors.length) toast.error(`${data.errors.length} problem(s) found in upload`);
      onUploaded();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    ref.current.value = "";
  };
  const sample = async () => {
    const { data } = await api.get("/web-orders/sample");
    const po = data.split("\r\n")[1].split("\t")[9];
    const store = po.slice(3, 7);
    downloadText(data, `S${store}_${po}_${new Date().toISOString().slice(0, 19).replace(/[-:]/g, "").replace("T", "_")}.txt`);
  };

  return (
    <div className="rounded-lg border bg-white p-5">
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); send(e.dataTransfer.files); }} onClick={() => ref.current.click()}
        data-testid="upload-dropzone"
        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors duration-200 ${drag ? "border-[#FF0000] bg-red-50" : "border-slate-300 hover:border-[#00573F] hover:bg-[#00573F]/[0.03]"}`}>
        <UploadCloud className={`h-9 w-9 ${drag ? "text-[#FF0000]" : "text-slate-400"}`} />
        <div className="mt-3 font-display text-lg font-semibold">{busy ? "Processing files…" : "Drop web order .txt files here"}</div>
        <div className="text-xs text-slate-500">Tab-delimited, one or many files · or click to browse</div>
        <input ref={ref} type="file" accept=".txt" multiple className="hidden" onChange={(e) => send(e.target.files)} data-testid="upload-file-input" />
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <details className="text-xs text-slate-500"><summary className="cursor-pointer font-semibold text-slate-700" data-testid="file-format-toggle">File format (12 tab-separated columns)</summary>
          <ol className="mt-2 list-decimal space-y-0.5 pl-5">{COLS.map((c) => <li key={c}>{c}</li>)}</ol></details>
        <Button variant="outline" size="sm" onClick={sample} data-testid="download-sample-file-btn"><Download className="mr-1 h-4 w-4" />Download sample file</Button>
      </div>
      {res && <Result res={res} />}
    </div>
  );
};
